import { Component, DestroyRef, HostListener, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { DictionaryKeyEnum } from '@core/enums/dictionary-key.enum';
import { OrderStatusKeyEnum } from '@core/enums/order-status-key.enum';
import { DepartmentDto } from '@core/models/department';
import { DictionaryModel, DictionarySet } from '@core/models/dictionary.model';
import { RegionModel } from '@core/models/region.model';
import { DictionaryNativeService } from '@core/services/data-services/dictionary.native.service';
import { PlannerNativeService } from '@core/services/data-services/planner.native.service';
import { RegionsNativeService } from '@core/services/data-services/regions.native.service';
import { addDays, getStartOfWeek } from '@core/utils/date.utils';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, interval, map, startWith, Subject, switchMap, tap } from 'rxjs';
import { GridColModel } from '../../models/grid-col-model';
import { OrderDeliveryConfiguration } from '../../models/order-delivery-configuration.model';
import { mapShipmentTaskDtoToModel, ShipmentTaskModel } from '../../models/shipment-task.model';
import { PlannerManagerService } from '../../services/planner-manager.service';
import { DaysModeEnum, PeriodsOffsetMap, MiddleDayMap } from '../../../_enums/days-mode.enum';
import { OverlayStatusEnum } from '../../../_enums/overlay-status.enum';
import { ROOT_SCOPE_VARIABLES } from '@core/settings/root-scope.settings';
import { Router } from '@angular/router';
import { PermissionsService } from '@core/services/permissions.service';


@Component({
  selector: 'order-delivery-view',
  standalone: false,
  templateUrl: './order-delivery-view.component.html',
  styleUrl: './order-delivery-view.component.scss'
})
export class OrderDeliveryViewComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() clientId!: number; // dane z bindingu w starym ng 

  taskOrderTypeSet$ = new BehaviorSubject<DictionarySet | null>(null);
  orderStatus$ = new BehaviorSubject<DictionaryModel[] | null>(null);
  vehicleCategorySet$ = new BehaviorSubject<DictionarySet | null>(null);
  wasteCodesSet$ = new BehaviorSubject<DictionarySet | null>(null);

  regions$ = new BehaviorSubject<RegionModel[] | null>(null);
  departments$ = new BehaviorSubject<DepartmentDto[] | null>(null);

  gridRows$ = new BehaviorSubject<GridColModel[]>([]);
  gridRowsFiltered$ = new BehaviorSubject<GridColModel[]>([]);
  displayedDays$ = new BehaviorSubject<Date[]>([]);

  plannerConfiguration$ = new BehaviorSubject<OrderDeliveryConfiguration>({
    daysMode: DaysModeEnum.WEEK_WORKING_DAYS,
    firstDay: getStartOfWeek(new Date()),
    lastDay: addDays(getStartOfWeek(new Date()), PeriodsOffsetMap[DaysModeEnum.WEEK_WORKING_DAYS]),
    middleDayOfThePeriod:  MiddleDayMap[DaysModeEnum.WEEK_WORKING_DAYS],
    clientId: -1,
  });

  daysModeOptions = [
    { value: DaysModeEnum.WEEK_WORKING_DAYS, displayName: 'Dni robocze' },
    { value: DaysModeEnum.WEEK, displayName: 'Tydzień' },
    { value: DaysModeEnum.DAY, displayName: 'Dzień' },
  ];

  mainSearchControl = new FormControl<string>('');
  regionControl = new FormControl<number | null>(null);
  departmentControl = new FormControl<number | null>(null);
  overlayStatus$ = new BehaviorSubject<OverlayStatusEnum>(OverlayStatusEnum.Idle);
  refreshShipmentTasksStream$ = new Subject<void>();
  OverlayStatusEnum = OverlayStatusEnum;


  lastUpdateDate$ = new BehaviorSubject<Date>(new Date());
  isAutoRefreshEnabled = true;
  isPageHidden = false;

  ROOT_SCOPE_VARIABLES = ROOT_SCOPE_VARIABLES;

  constructor(
    private readonly _regionsNativeService: RegionsNativeService,
    private readonly _dictionaryNativeService: DictionaryNativeService,
    private readonly _plannerManagerService: PlannerManagerService,
    private readonly _plannerNativeService: PlannerNativeService,
    private readonly _permissionService: PermissionsService,
    private readonly _router: Router,
  ) {
    if (!this._permissionService.isPermissionEnabled(ROOT_SCOPE_VARIABLES.PERMISSIONS.PLANNING_DAILYPLAN.LOADINGUNLODINGVIEW)) {
      this._router.navigate(['/']);
    }
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilitychange() {
    this.isPageHidden = document.hidden;
  }

  ngOnInit(): void {
    this.plannerConfiguration$.next({      
      ...this.plannerConfiguration$.value,
      clientId: this.clientId,
    });
    this.loadDictionaries();
    this.initDaysHeaderCalculation();
    this.initClientSideSearchControlls();
    this.initDriversRouteLoading();
    this.initAutoRefresh();
  }

  pageChanged(goingForward = true): void {
    const { daysMode, firstDay, lastDay } = this.plannerConfiguration$.value;
    const daysOffset = daysMode === DaysModeEnum.DAY ? 1 : 7;
    const adjustedDays = goingForward ? daysOffset : -daysOffset;

    this.plannerConfiguration$.next({
      ...this.plannerConfiguration$.value,
      firstDay: addDays(firstDay, adjustedDays),
      lastDay: addDays(lastDay, adjustedDays),
    });
  }

  daysModeChanged($event: any) {
    const daysModeNewValue = ($event.target as HTMLSelectElement).value as DaysModeEnum;
    const firstDay = daysModeNewValue !== DaysModeEnum.DAY ? getStartOfWeek(this.plannerConfiguration$.value.firstDay) : this.plannerConfiguration$.value.firstDay;

    this.plannerConfiguration$.next(
      {
        ...this.plannerConfiguration$.value,
        daysMode: daysModeNewValue,
        firstDay: firstDay,
        lastDay: addDays(firstDay, PeriodsOffsetMap[daysModeNewValue]),
        middleDayOfThePeriod: MiddleDayMap[daysModeNewValue]
      }
    )
  }

  checkIfClickedOnBackdrop($event: MouseEvent) {
    if (this.overlayStatus$.value === OverlayStatusEnum.Overlay) {
      $event.preventDefault();
      this.overlayStatus$.next(OverlayStatusEnum.Idle);
    }
  }

  refreshRoutesClick(): void {
    this.refreshShipmentTasksStream$.next();
  }



  maskAsCompleted($event: ShipmentTaskModel, markAsCompleted: boolean): void {
    this.overlayStatus$.next(OverlayStatusEnum.Working);

    this._plannerNativeService.setTaskScaleStatus({ TaskId: $event.TaskId, IsScaleRealised: markAsCompleted })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({
        next: () => {
          this.markAsRealized($event.TaskId, markAsCompleted);
          this.overlayStatus$.next(OverlayStatusEnum.Idle);
        },
        error: () => this.overlayStatus$.next(OverlayStatusEnum.Idle)
      }));
  }

  plannerDateRangeChanged($event: { start: Date, end: Date }) {
    this.plannerConfiguration$.next({
      ...this.plannerConfiguration$.value,
      firstDay: $event.start,
      lastDay: addDays($event.end, 1), // w planerze lastdate to godzina 00:00 nastepnego dnia, więc datę z kalendarza musimy dać +1 
    })
  }

  private markAsRealized(taskId: number, markAsCompleted: boolean): void {
    // na razie tak "łopatologicznie", w przyszłości trzeba by to inaczej obsłużyć
    const clonedValues = cloneDeep(this.gridRows$.value);
    const task = clonedValues.flatMap(c => c.shipmentTask).find(s => s.TaskId === taskId);
    if (task) {
      task.IsScaleRealised = markAsCompleted
    }

    this.gridRows$.next(clonedValues);
  }

  private initClientSideSearchControlls(): void {
    combineLatest([
      this.gridRows$,
      this.mainSearchControl.valueChanges.pipe(debounceTime(400), startWith(''), map(s => s ? s.toLowerCase() : '')),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef),
        map(([gridRows, mainSearchCriteria]) => {
          let results = cloneDeep(gridRows);

          if (mainSearchCriteria) {
            const newResults: GridColModel[] = [];
            for (let gridRow of results) {

              const filteredShipmentTasks = gridRow.shipmentTask.filter(shipmentTask => this.filterOrderBySearchCriteria(shipmentTask, mainSearchCriteria));
              newResults.push({
                day: gridRow.day,
                shipmentTask: cloneDeep(filteredShipmentTasks),
              })
            }
            results = [...newResults]
          }

          return results;
        })).subscribe({
          next: filteredRows => {
            this.gridRowsFiltered$.next(filteredRows);
          }
        });
  }

  private filterOrderBySearchCriteria(shipmentTask: ShipmentTaskModel, mainSearchCriteria: string): boolean {
    return shipmentTask.CarrierNormalized.includes(mainSearchCriteria)
      || shipmentTask.PointNameNormalized.includes(mainSearchCriteria)
      || shipmentTask.AddressNormalized.includes(mainSearchCriteria)
      || shipmentTask.DriverNameNormalized.includes(mainSearchCriteria)
      || shipmentTask.BDONormalized.includes(mainSearchCriteria)
      || shipmentTask.DriverPhoneNormalized.includes(mainSearchCriteria)
      || shipmentTask.VehicleNumberNormalized.includes(mainSearchCriteria)
      || shipmentTask.TrailerNumberNormalized.includes(mainSearchCriteria)
      || shipmentTask.NoteCargoNormalized.includes(mainSearchCriteria);
  }

  private initDaysHeaderCalculation(): void {
    this.plannerConfiguration$.pipe(takeUntilDestroyed(this.destroyRef),
      map(conf => this._plannerManagerService.generateDaysHeaderObjects(conf.firstDay, conf.daysMode)))
      .subscribe({
        next: v => {
          this.displayedDays$.next(v)
        }
      });
  }

  private initDriversRouteLoading(): void {
    const getLoadingStream = combineLatest(
      [this.plannerConfiguration$,
      this.regionControl.valueChanges,
      this.departmentControl.valueChanges,
      this.orderStatus$.pipe(filter(s => !!s)),
      this.refreshShipmentTasksStream$.pipe(startWith(() => { }))
      ])
      .pipe(distinctUntilChanged(),
        filter(([_, regionId, departmentId]) => !!regionId && regionId > 0 && !!departmentId && departmentId > 0),
        tap(() => this.overlayStatus$.next(OverlayStatusEnum.Working)),
        switchMap(([plannerConfiguration, regionId, departmentId, orderStatusSet]) => {

          const desiredOrderStatuses = [OrderStatusKeyEnum.IN_PROGRESS, OrderStatusKeyEnum.COMPLETED, OrderStatusKeyEnum.INVOICED];
          const stausesKeys = orderStatusSet!.filter(s => desiredOrderStatuses.includes(s.KeyName as OrderStatusKeyEnum));

          return this._plannerNativeService.getLoadings({
            clientId: plannerConfiguration.clientId,
            departmentId: departmentId!,
            regionId: regionId!,
            from: plannerConfiguration.firstDay,
            to: plannerConfiguration.lastDay,
            statuses: stausesKeys.map(s => s.Id)
          }).pipe(map(loadings => ({ loadings: loadings, plannerConfiguration })))
        }),
      )


    combineLatest([
      getLoadingStream,
      this.taskOrderTypeSet$.pipe(filter(r => !!r)),
      this.vehicleCategorySet$.pipe(filter(r => !!r)),
      this.wasteCodesSet$.pipe(filter(r => !!r)),
    ])
      .subscribe({
        next: ([resp, taskOrderTypeSet, vehicleCategorySet, wasteCodesSet]) => {
          const updatedRows = this._plannerManagerService.generateLoadingsForDays(resp.loadings.Tasks
            .map(l => mapShipmentTaskDtoToModel(l, taskOrderTypeSet!, vehicleCategorySet!, wasteCodesSet!)), resp.plannerConfiguration);
          this.gridRows$.next(updatedRows);
          this.overlayStatus$.next(OverlayStatusEnum.Idle);
          this.lastUpdateDate$.next(new Date());
        },
        error: () => this.overlayStatus$.next(OverlayStatusEnum.Idle)
      }
      );
  }

  private loadDictionaries(): void {
    this._dictionaryNativeService.getDictionaryByKey({ key: DictionaryKeyEnum.TASK_ORDER_TYPE })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          this._plannerManagerService.init({ taskOrderType: resp.Items });
          this.taskOrderTypeSet$.next(this._dictionaryNativeService.mapDictionaryById(resp.Items));
        }
      });

    this._dictionaryNativeService.getDictionaryByKey({ key: DictionaryKeyEnum.ORDER_STATUS })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: orderStatus => {
          this.orderStatus$.next(orderStatus.Items);
        }
      });

    this._regionsNativeService.getRegions({ clientId: this.plannerConfiguration$.value.clientId })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: regions => {
          this.regions$.next(regions);
          if (regions.length > 0) {
            this.regionControl.setValue(regions[0].Id);
          }
        }
      });

    this._regionsNativeService.getDepartments({ clientId: this.plannerConfiguration$.value.clientId })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: departments => {
          this.departments$.next(departments);
          if (departments.length > 0) {
            this.departmentControl.setValue(departments[0].Id);
          }
        }
      });

    this._dictionaryNativeService.getDictionaryByKey({ key: DictionaryKeyEnum.VEHICLE_CATEGORY })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: vehicleCategories => {
          this.vehicleCategorySet$.next(this._dictionaryNativeService.mapDictionaryById(vehicleCategories.Items));
        }
      });

    this._dictionaryNativeService.getDictionaryByKey({ key: DictionaryKeyEnum.WASTECODES })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: wastecodes => {
          this.wasteCodesSet$.next(this._dictionaryNativeService.mapDictionaryById(wastecodes.Items));
        }
      });
  }

  private initAutoRefresh(): void {
    interval(1000).pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(() => this.isAutoRefreshEnabled && !this.isPageHidden),
      filter(() => this.overlayStatus$.value === OverlayStatusEnum.Idle),
    ).subscribe(() => {
      const diffInMs = new Date().getTime() - this.lastUpdateDate$.value.getTime();

      if (diffInMs > 50000) {
        this.refreshShipmentTasksStream$.next();
      }
    });
  }


}
